import * as React from 'react';

import {ScrollableContainerWithShadow} from '../layout/scrollable-container-with-shadow/scrollable-container-with-shadow';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {LoadingPage} from '../loading-page/loading-page';
// @ts-expect-error
import {LocationEnum, Popover} from '../popover/popover';

import {BulkSelectButton} from './bulk-select-button';
import {getUpdatedItemsList, getBulkUpdatedItemsList} from './helpers';
import {ListItem} from './list-item';
import type {Item} from './types';

import styles from './mini-list-popover.module.css';

export type Props = {
    anchor: any;
    headerText?: string;
    collection: Item[];
    renderRow: (item: Item) => React.ReactElement;
    // For controlled (by parent) input - do not pass both, pass none for readonly version
    onSelect?: (id: string) => void;
    // For uncontrolled (by parent) input - do not pass both, pass none for readonly version
    onBlur?: (updatedItems: Item[]) => void;
    isSaving?: boolean;
    onClose?: () => void;
    location?: LocationEnum;
    disableOverlayClickToBlur?: boolean;
};

export function MiniListPopover(props: Props) {
    const {
        collection,
        anchor,
        renderRow,
        onSelect,
        onBlur,
        isSaving,
        onClose,
        location = 'bottom-left',
        disableOverlayClickToBlur,
    } = props;

    // Internal state for uncontrolled (by parent) input, if the list is selectable
    const [selectableItemsList, setSelectableItemsList] = React.useState<Item[] | null | undefined>(
        undefined
    );

    React.useEffect(() => {
        if (onBlur && !selectableItemsList) {
            setSelectableItemsList(collection);
        }
    }, [collection, selectableItemsList, onBlur]);

    // @ts-expect-error
    let handleOnSelect;
    if (onSelect) {
        handleOnSelect = (id: string) => onSelect(id);
    } else if (selectableItemsList) {
        handleOnSelect = (id: string) => {
            setSelectableItemsList((prevState) =>
                prevState ? getUpdatedItemsList(prevState, id) : undefined
            );
        };
    }

    const shouldListScroll = collection.length > 5;
    const Container = shouldListScroll ? ScrollableContainerWithShadow : 'div';
    const scrollProps = shouldListScroll
        ? {
              direction: 'vertical',
              maxContainerSize: 220,
              showReverseGradientShadow: true,
          }
        : {};

    const handleBlur = () => {
        if (onBlur) {
            onBlur(selectableItemsList ? selectableItemsList : collection);
        }

        if (onClose) {
            onClose();
        }
    };

    const handleBulkSelection = (selectAll: boolean) => {
        setSelectableItemsList((prevState) =>
            prevState ? getBulkUpdatedItemsList(prevState, selectAll) : undefined
        );
    };

    return anchor && anchor.current ? (
        <Popover
            anchor={anchor.current}
            location={location}
            onBlur={handleBlur}
            disableOverlayClickToBlur={disableOverlayClickToBlur}
        >
            <div className={styles['mini-list-popover']}>
                {isSaving ? (
                    <div className={styles['loading-container']}>
                        <LoadingPage padding={0} />
                    </div>
                ) : undefined}
                {/* @ts-expect-error */}
                <Container {...scrollProps}>
                    {props.headerText ? (
                        <div className={styles.header}>{props.headerText}</div>
                    ) : undefined}
                    <StackLegacy spacing={4}>
                        <BulkSelectButton
                            // @ts-expect-error
                            collection={props.collection}
                            selectableItemsList={selectableItemsList}
                            onClick={handleBulkSelection}
                        />
                        {(selectableItemsList || collection).map((item) => (
                            <ListItem
                                key={item.id}
                                id={item.id}
                                content={renderRow(item)}
                                isChecked={item.isChecked}
                                // @ts-expect-error
                                onSelect={handleOnSelect}
                                isDisabled={isSaving}
                            />
                        ))}
                    </StackLegacy>
                </Container>
            </div>
        </Popover>
    ) : null;
}
